import { AloftDateJSON } from '@aloft/shared-objects';
import {
  AloftDate,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PropertyData,
  PropertyDataWithOptionalReadonly,
  SourceType,
  SubjectData,
} from '@aloft/toolkit-shared';
import { IconName } from '@aloft/toolkit-sui';

import { startCase } from 'lodash';

import { setPropertyData } from '../../redux/features/propertyDataSlice.ts';
import { AppDispatch, PropertyDataState } from '../../redux/types.ts';
import { useSubjectCraftsmanPropertyToCompSelectionProperty } from '../RecommendedCompSelection/hooks/useSubjectCraftsmanPropertyToCompSelectionProperty.ts';
import { calcSimilarity } from '../RecommendedCompSelection/internal/similarity/calcSimilarity.ts';
import { compsToCompSelectionProps } from '../RecommendedCompSelection/utils/compsToCompSelectionProps.ts';
import { PropertyGridColumnDefinition, PropertyGridForm, schPropertyGridForm } from './types.ts';

export const getDefaultFormColumns = (
  compSequence: string[],
  dataSources: SourceType[],
): PropertyGridColumnDefinition[] =>
  [PROPERTY_GRID_SUBJECT_PROPERTY, ...compSequence].flatMap((propertyId) => [
    {
      propertyId,
      type: 'property',
    },
    ...dataSources.map((source) => ({
      propertyId,
      type: source,
    })),
  ]);

export const getDataSourceDisplayInfo = (
  dataSource: SourceType,
  propertyData?: PropertyGridColumnDefinition['propertyData'],
): { icon: IconName; text: string; subText?: string } => {
  switch (dataSource) {
    case 'abode_mls': {
      const icon = 'mls-listing';
      const text = 'MLS';
      const listing = propertyData?.readonlyData?.backupListingData?.listing;

      if (!listing || !listing.ListingId || !listing.MLSName) return { icon, text };

      return {
        icon,
        text,
        subText: `${listing.MLSName?.toLocaleUpperCase() ?? ''} # ${listing.ListingId.toString()}`,
      };
    }
    case 'abode_public_record': {
      const county = propertyData?.editableData?.propertyIdentification?.address?.CountyName;
      const parcelNumber = propertyData?.editableData?.propertyIdentification?.parcelNumber;
      const subText =
        (county ? `${county} County` : undefined) ||
        (parcelNumber ? `APN: ${parcelNumber}` : undefined);

      return {
        icon: 'public-record',
        text: 'Public Record',
        subText,
      };
    }
    case 'xml':
      return { icon: 'file-xml', text: 'XML Data' };
    default:
      return { icon: 'user-input', text: 'Unknown Data Source' };
  }
};

export const getSelectSourceTooltip = (sourceType: SourceType) => {
  switch (sourceType) {
    case 'abode_mls':
      return 'MLS';
    case 'abode_public_record':
      return 'Public Records';
    default:
      return startCase(sourceType);
  }
};

// We call this function in each field to avoid an erroneous field preventing updating the other fields
export const handleUpdatePropertyData = (
  propertyData: PropertyDataWithOptionalReadonly | SubjectData,
  propertyId: string,
  formValues: PropertyGridForm,
  dispatch: AppDispatch,
) => {
  const parsedFormValues = schPropertyGridForm.safeParse(formValues);
  if (!parsedFormValues.success) {
    console.error('Invalid form values:', parsedFormValues.error);
    return;
  }

  let propertyDataState: PropertyDataState;

  // eslint-disable-next-line no-param-reassign
  if (!propertyData.readonlyData) propertyData.readonlyData = {};

  setDefaultCraftsmanQualityWhenMissing(propertyData);

  if (propertyId === PROPERTY_GRID_SUBJECT_PROPERTY) {
    propertyDataState = {
      id: propertyId,
      subjectData: propertyData as SubjectData,
    };
  } else {
    propertyDataState = {
      id: propertyId,
      compData: propertyData as PropertyData,
    };
  }

  dispatch(setPropertyData(propertyDataState));
};

const setDefaultCraftsmanQualityWhenMissing = (
  propertyData: PropertyDataWithOptionalReadonly | SubjectData,
) => {
  const { improvements } = propertyData.editableData;
  if (!improvements.craftsmanQuality) {
    improvements.craftsmanQuality = improvements.uadQuality;
  }
};

export const getSimilarityScore = (
  subject: SubjectData,
  effectiveDate: AloftDateJSON,
  propertyId: string,
  propertyData: PropertyData,
) => {
  const subjectCompSelectionProperty = useSubjectCraftsmanPropertyToCompSelectionProperty(
    subject,
    AloftDate.fromIsoString(effectiveDate.isoString),
  );

  const compSelectionProperty = compsToCompSelectionProps(
    [propertyId],
    { [propertyId]: propertyData },
    subjectCompSelectionProperty,
  )[0];

  if (!compSelectionProperty) return '-';
  return calcSimilarity(subjectCompSelectionProperty, compSelectionProperty).toFixed(0);
};
